import React, { useState } from 'react';
import { BookOpen, CheckSquare, GraduationCap, PlayCircle, LayoutGrid, ChevronRight } from 'lucide-react';
import FocusView from './FocusView';
import ContentPlaybooks from './BizBuildingProgramView/ContentPlaybooks';
import ProgressIntelligence from './BizBuildingProgramView/ProgressIntelligence';
import DailyRoutineExecution from './BizBuildingProgramView/DailyRoutineExecution';

const BizBuildingProgramView = ({ user }) => {
    const [activeTool, setActiveTool] = useState('overview');

    const tools = [
        {
            id: 'focus',
            label: 'Focus Control Room',
            description: 'Manage the 14 operational blocks and 7 Laws of Success',
            icon: CheckSquare,
            color: 'bg-blue-100 text-blue-600',
            type: 'operational'
        },
        {
            id: 'playbooks',
            label: 'Content Playbooks',
            description: 'Step-by-step implementation guides for business growth',
            icon: BookOpen,
            color: 'bg-purple-100 text-purple-600',
            type: 'educational'
        },
        {
            id: 'certification',
            label: 'Progress Intelligence',
            description: 'Track your journey through the BizBuilding Program',
            icon: GraduationCap,
            color: 'bg-emerald-100 text-emerald-600',
            type: 'educational'
        },
        {
            id: 'execution',
            label: 'Daily Routine Execution',
            description: 'Automated sequence of the 9 program tasks',
            icon: PlayCircle,
            color: 'bg-amber-100 text-amber-600',
            type: 'operational'
        }
    ];

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex items-center gap-4 mb-2">
                <div className="bg-slate-900 p-3 rounded-2xl text-white">
                    <LayoutGrid size={24} />
                </div>
                <div>
                    <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">BizBuilding Program</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1">
                        AI-Driven Business Development Framework
                    </p>
                </div>
            </div>

            {activeTool === 'overview' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {tools.map(tool => (
                        <button
                            key={tool.id}
                            onClick={() => setActiveTool(tool.id)}
                            className="group flex items-center gap-5 p-6 rounded-[2.5rem] bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all text-left active:scale-[0.98]"
                        >
                            <div className={`p-4 rounded-3xl ${tool.color} transition-transform group-hover:scale-110`}>
                                <tool.icon size={24} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight group-hover:text-blue-700 transition-colors">
                                    {tool.label}
                                </h3>
                                <p className="text-slate-400 text-xs font-medium leading-relaxed">
                                    {tool.description}
                                </p>
                            </div></button>
                    ))}

                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-8 rounded-[2.5rem] text-white flex flex-col justify-center space-y-4 relative overflow-hidden">
                        <div className="relative z-10">
                            <h3 className="text-xl font-black uppercase tracking-tight">Current Stage: Growth</h3>
                            <p className="text-blue-100 text-sm opacity-80">You are currently optimizing for scalability and referral systems.</p>
                        </div>
                        <div className="absolute -right-10 -bottom-10 opacity-20 rotate-12">
                            <GraduationCap size={150} />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <button
                        onClick={() => setActiveTool('overview')}
                        className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors text-xs font-black uppercase tracking-widest"
                    >
                        <ChevronRight size={14} className="rotate-180" />
                        Back to Program Hub
                    </button>

                    {activeTool === 'focus' && <FocusView />}
                    {activeTool === 'playbooks' && <ContentPlaybooks />}
                    {activeTool === 'certification' && <ProgressIntelligence />}
                    {activeTool === 'execution' && <DailyRoutineExecution />}
                </div>
            )}
        </div>
    );
};

export default BizBuildingProgramView;
